import {type AccountType} from "@hosttools/core/constant";

import * as AirbnbIconSvg from "../assets/airbnb-icon.svg";
import * as AirbnbIconCircle from "../assets/airbnb-logo-solid-circle.svg";
import * as AirbnbBrand from "../assets/airbnb-logo.svg";
import * as AugustIconSvg from "../assets/august-icon.svg";
import * as AugustIconCircle from "../assets/august-logo-solid-circle.svg";
import * as AugustBrand from "../assets/august-logo.svg";
import * as BookingIconSvg from "../assets/booking-icon.svg";
import * as BookingIconCircle from "../assets/booking-logo-solid-circle.svg";
import * as BookingBrand from "../assets/booking-logo.svg";
import * as CalendarIconSvg from "../assets/calendar-icon.svg";
import * as ChannelConnectorLogo from "../assets/channel-connector-logo.svg";
import * as HoufyIconCircle from "../assets/houfy-icon-circle.svg";
import * as HoufyIconSvg from "../assets/houfy-icon.svg";
import * as HoufyBrand from "../assets/houfy-logo.svg";
import * as SeamIcon from "../assets/lock.svg";
import * as HostToolsLogo from "../assets/logo-icon-square.svg";
import * as VRBOIconSvg from "../assets/vrbo-icon.svg";
import * as VRBOIconCircle from "../assets/vrbo-logo-solid-circle.svg";
import * as VRBOBrand from "../assets/vrbo-logo.svg";
import {
    AirbnbCircleIcon,
    AugustCircleIcon,
    BookingCircleIcon,
    CalendarIcon,
    ChannelConnectorLogoIcon,
    HostToolsLogoIcon,
    HoufyCircleIcon,
    SeamLogoIcon,
    VRBOCircleIcon
} from "../react/components/Icons";

export type FCSVG = typeof AirbnbIconSvg.ReactComponent;

export interface ChannelObject {
    label: string;
    value: AccountType;
    logoBackgroundColor?: string;
    logo: string;
    bgAvatarClassName?: string;
    /**
     * @deprecated
     */
    bgClass: string;
    logoBrand: string;
    Logo: FCSVG;
}

const logoMaker = (svg: typeof AirbnbIconSvg) => {
    const {default: logo, ReactComponent: Logo} = svg;

    return typeof logo === "string" ? {logo, Logo} : {logo: "", Logo: logo};
};

const logoCircleMaker = (svg: typeof AirbnbIconCircle) => {
    const {default: logoCircle, ReactComponent: LogoCircle} = svg;

    return typeof logoCircle === "string"
        ? {logoCircle, LogoCircle}
        : {logoCircle: "", LogoCircle: logoCircle};
};

const logoBrandMaker = ({default: urlOrSvg, ReactComponent}: typeof AirbnbIconSvg) =>
    typeof urlOrSvg === "string"
        ? {logoBrand: urlOrSvg, LogoBrand: ReactComponent}
        : {logoBrand: "", LogoBrand: urlOrSvg};

export const channelInternal = {
    label: "Internal",
    value: "internal",
    ...logoMaker(HostToolsLogo),
    ...logoCircleMaker(HostToolsLogo),
    LogoIcon: HostToolsLogoIcon,
    LogoCircleIcon: HostToolsLogoIcon,
    logoBrand: undefined,
    LogoBrand: undefined,
    bgClass: "bg-internal",
    bgAvatarClassName: "bg-internal-avatar"
} as const;

export const channelDirect = {
    ...channelInternal,
    value: "direct",
    label: "Direct",
    bgClass: "bg-direct",
    bgAvatarClassName: "bg-direct-avatar"
} as const;

export const channelsMap = {
    internal: channelInternal,
    direct: channelDirect,
    Airbnb: {
        label: "Airbnb",
        value: "Airbnb",
        ...logoMaker(AirbnbIconSvg),
        ...logoCircleMaker(AirbnbIconCircle),
        ...logoBrandMaker(AirbnbBrand),
        LogoIcon: AirbnbCircleIcon,
        bgClass: "bg-airbnb",
        bgAvatarClassName: "bg-Airbnb-avatar"
    },
    Booking: {
        label: "Booking",
        value: "Booking",
        ...logoMaker(BookingIconSvg),
        ...logoCircleMaker(BookingIconCircle),
        ...logoBrandMaker(BookingBrand),
        LogoIcon: BookingCircleIcon,
        bgClass: "bg-booking",
        bgAvatarClassName: "bg-Booking-avatar"
    },
    HomeAway: {
        label: "VRBO",
        value: "HomeAway",
        ...logoMaker(VRBOIconSvg),
        ...logoCircleMaker(VRBOIconCircle),
        ...logoBrandMaker(VRBOBrand),
        LogoIcon: VRBOCircleIcon,
        bgClass: "bg-homeaway",
        bgAvatarClassName: "bg-HomeAway-avatar"
    },
    Houfy: {
        label: "Houfy",
        value: "Houfy",
        ...logoMaker(HoufyIconSvg),
        ...logoCircleMaker(HoufyIconCircle),
        ...logoBrandMaker(HoufyBrand),
        LogoIcon: HoufyCircleIcon,
        bgClass: "bg-houfy",
        bgAvatarClassName: "bg-Houfy-avatar"
    },
    ChannelConnector: {
        label: "Other Channels",
        value: "ChannelConnector",
        ...logoMaker(ChannelConnectorLogo),
        ...logoBrandMaker(ChannelConnectorLogo),
        LogoIcon: ChannelConnectorLogoIcon,
        bgClass: "bg-internal",
        bgAvatarClassName: "bg-internal-avatar"
    },
    August: {
        label: "August",
        value: "August",
        ...logoMaker(AugustIconSvg),
        ...logoCircleMaker(AugustIconCircle),
        ...logoBrandMaker(AugustBrand),
        LogoIcon: AugustCircleIcon,
        bgClass: "bg-august"
    },
    Seam: {
        label: "Seam",
        value: "Seam",
        ...logoMaker(SeamIcon),
        ...logoCircleMaker(SeamIcon),
        ...logoBrandMaker(SeamIcon),
        LogoIcon: SeamLogoIcon,
        bgClass: "bg-august"
    }
} as const;

export type ChannelMapType = typeof channelsMap;

export type OtherChannelObject = {
    label: string;
    value: string;
};

export type ICalChannelObject = {
    label: string;
    value: "icalendar";
    Logo: FCSVG;
    LogoIcon: React.ElementType;
    LogoCircle: FCSVG;
    LogoCircleIcon: React.ElementType;
};

export function buildChannel<T extends AccountType | "internal" | "direct">(
    channel: T
): ChannelMapType[T];

export function buildChannel(
    channel: string & {},
    isChannelConnector?: boolean
): OtherChannelObject | ICalChannelObject;

export function buildChannel<T extends AccountType | "internal" | "direct">(
    channel: T,
    isChannelConnector?: boolean
) {
    if (isChannelConnector) {
        return {
            label: channel,
            value: channel
        } as const;
    }

    // source imported via iCal will look like this: www.airbnb.com.au
    return (
        channelsMap[channel] ??
        ({
            label: channel,
            value: "icalendar",
            ...logoMaker(CalendarIconSvg),
            ...logoCircleMaker(CalendarIconSvg),
            ...logoBrandMaker(CalendarIconSvg),
            LogoIcon: CalendarIcon,
            LogoCircleIcon: CalendarIcon,
            logoBrand: undefined,
            LogoBrand: undefined,
            bgClass: "bg-icalendar",
            bgAvatarClassName: "bg-icalendar-avatar"
        } as const)
    );
}

export function isICalendarObject(
    channelObject: OtherChannelObject | ICalChannelObject
): channelObject is ICalChannelObject {
    if (channelObject.value === "icalendar") {
        return true;
    }

    return false;
}
